import React, { useEffect, useState } from "react"
import propTypes from "prop-types"
import {
  Badge,
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import EmailSelectorList from "./modalNewBilling/EmailSelectorList"

const isValidEmail = (email) => /^\S+@\S+\.\S+$/.test(email)

const formatHistoryDate = (date) => {
  if (!date) return "Sin fecha"
  return new Date(date).toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const ModalSendBillingMail = ({
  isOpen,
  toggle,
  invoiceLabel,
  initialEmails,
  onSend,
  isLoading,
  emailHistory,
  isHistoryLoading,
}) => {
  const [emails, setEmails] = useState([""])
  const [error, setError] = useState("")
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const nextEmails = Array.isArray(initialEmails) && initialEmails.length > 0 ? initialEmails.slice(0, 5) : [""]
      setEmails(nextEmails)
      setError("")
      setShowErrors(false)
    }
  }, [isOpen, initialEmails])

  const handleEmailChange = (index, value) => {
    setShowErrors(false)
    setEmails((prev) => {
      const copy = [...prev]
      copy[index] = value
      return copy
    })
  }

  const handleAddEmail = () => {
    setEmails((prev) => (prev.length < 5 ? [...prev, ""] : prev))
  }

  const handleRemoveEmail = (index) => {
    setEmails((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    const trimmed = emails.map((e) => e.trim())
    const filtered = trimmed.filter(Boolean)
    const invalid = trimmed.find((e) => e && !isValidEmail(e))

    if (filtered.length === 0 || invalid) {
      setShowErrors(true)
      return
    }

    if (filtered.length > 5) {
      setShowErrors(true)
      return
    }

    setError("")
    await onSend(filtered)
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered backdrop="static" size="lg">
      <ModalHeader toggle={toggle} charcode="X">
        Enviar factura por correo
      </ModalHeader>
      <ModalBody>
        <p className="mb-2">
          {invoiceLabel ? `Folio: ${invoiceLabel}` : "Factura seleccionada"}
        </p>

        <Row>
          <Col md="7">
            <FormGroup>
              <Label
                for="billing-email-recipients"
                style={{ fontSize: "1.1rem", fontWeight: 600 }}
              >
                Correos destinatarios
              </Label>
              <EmailSelectorList
                emails={emails}
                isLoading={isLoading}
                handleEmailChange={handleEmailChange}
                handleAddEmail={handleAddEmail}
                handleRemoveEmail={handleRemoveEmail}
                errors={emails.map((e) => {
                  const t = e.trim()
                  if (t !== "" && !isValidEmail(t)) return "Correo no valido."
                  if (showErrors && t === "") return "Ingresa un correo."
                  return ""
                })}
              />
            </FormGroup>

            <small className="text-muted d-block mb-2">Maximo 5 destinatarios.</small>

            {error ? <div className="text-danger mt-2">{error}</div> : null}
          </Col>

          <Col md="5">
            <div
              style={{
                borderLeft: "1px solid #e9ecef",
                minHeight: "100%",
                paddingLeft: "16px",
              }}
            >
              <h6 className="mb-3">Historial de envios</h6>
              {isHistoryLoading ? (
                <div className="text-muted">Cargando historial...</div>
              ) : emailHistory?.length > 0 ? (
                <div style={{ maxHeight: "300px", overflowX: "hidden", overflowY: "auto" }}>
                  {emailHistory.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        borderBottom: "1px solid #edf0f2",
                        marginBottom: "10px",
                        paddingBottom: "10px",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center flex-wrap mb-1">
                        <strong style={{ fontSize: "0.85rem" }}>
                          {formatHistoryDate(item.created_at)}
                        </strong>
                        <Badge color={item.status === "sent" ? "success" : "danger"}>
                          {item.status === "sent" ? "Enviado" : "Error"}
                        </Badge>
                      </div>
                      <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                        {item.user_name || item.user_email || "Usuario no disponible"}
                      </div>
                      <div style={{ fontSize: "0.85rem", wordBreak: "break-word" }}>
                        {(item.recipients || []).join(", ")}
                      </div>
                      {item.error_message ? (
                        <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                          {item.error_message}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted">Sin envios registrados.</div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={toggle} disabled={isLoading}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? "Enviando..." : "Enviar"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

ModalSendBillingMail.propTypes = {
  isOpen: propTypes.bool,
  toggle: propTypes.func,
  invoiceLabel: propTypes.oneOfType([propTypes.string, propTypes.number]),
  initialEmails: propTypes.arrayOf(propTypes.string),
  onSend: propTypes.func.isRequired,
  isLoading: propTypes.bool,
  emailHistory: propTypes.arrayOf(propTypes.object),
  isHistoryLoading: propTypes.bool,
}

export default ModalSendBillingMail
